/* ===========================================================
   editor/tabs.js — tabs inside one doc (his ask, 2026-10-28)

   Each tab is its own Yjs fragment in the same doc, so tabs save,
   sync and co-edit like everything else, and versions keep them all.
     tab "default" → fragment "default" (the first tab: the doc's
                     title, snippet and search come from it)
     tab <id>      → fragment "tab-<id>"
   The list lives in a Y.Array "tabs" of { id, title, kind? }. A doc
   that never added a tab has no list: it's just "Tab 1".
   kind 'claude' is the Updates doc's reply tab (Claude writes there).
   =========================================================== */

import { newId } from '../../shared/protocol.js';

export const FIRST = { id: 'default', title: 'Tab 1' };
export const fieldOf = id => (!id || id === 'default' ? 'default' : `tab-${id}`);

export function tabsOf(ydoc) {
  const list = ydoc.getArray('tabs').toArray().filter(t => t && t.id);
  return list.length ? list : [FIRST];
}

function ensureList(ydoc) {
  const arr = ydoc.getArray('tabs');
  if (!arr.length) arr.push([FIRST]);
  return arr;
}

export function addTab(ydoc, title, extra = {}) {
  const tab = { id: newId(), title: (title || '').trim().slice(0, 60) || `Tab ${tabsOf(ydoc).length + 1}`, ...extra };
  ydoc.transact(() => ensureList(ydoc).push([tab]), 'dp:tabs');
  return tab;
}

function replaceAt(ydoc, id, change) {
  ydoc.transact(() => {
    const arr = ensureList(ydoc);
    const i = arr.toArray().findIndex(t => t.id === id);
    if (i < 0) return;
    const next = change(arr.get(i), i, arr);
    if (next === undefined) return;
    arr.delete(i, 1);
    if (next) arr.insert(i, [next]);
  }, 'dp:tabs');
}

export const renameTab = (ydoc, id, title) => replaceAt(ydoc, id, t => ({ ...t, title: (title || '').trim().slice(0, 60) || t.title }));

export function moveTab(ydoc, id, dir) {
  ydoc.transact(() => {
    const arr = ensureList(ydoc);
    const list = arr.toArray();
    const i = list.findIndex(t => t.id === id), j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    const [t] = list.splice(i, 1);
    list.splice(j, 0, t);
    arr.delete(0, arr.length);
    arr.insert(0, list);
  }, 'dp:tabs');
}

/** Removes a tab and its text. The first tab can't go (it holds the title). */
export function deleteTab(ydoc, id) {
  if (id === 'default') return false;
  ydoc.transact(() => {
    const frag = ydoc.getXmlFragment(fieldOf(id));
    if (frag.length) frag.delete(0, frag.length);
    const arr = ensureList(ydoc);
    const i = arr.toArray().findIndex(t => t.id === id);
    if (i >= 0) arr.delete(i, 1);
  }, 'dp:tabs');
  return true;
}

/** Headings in an editor, for the outline: [{ level, text, pos }]. */
export function headingsIn(editor) {
  const out = [];
  editor?.state.doc.descendants((node, pos) => {
    if (node.type.name === 'heading' && node.textContent.trim()) out.push({ level: node.attrs.level, text: node.textContent.trim().slice(0, 80), pos });
    return node.isBlock && node.type.name !== 'heading';
  });
  return out;
}
